import { CoverPicture, RightSideModal, RightSideModalTitle } from 'components';
import { IAppointmentWithCustomer } from 'models/shop/appointments';
import * as c from 'models/constants';
import * as React from 'react';
import { IStore } from 'redux/IStore';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { ITranslateProps, InjectedFormProps } from 'models/common';
import { InjectedManualBookingModalProps, InjectedQueryParams, ManualBookingScreen } from '../';
import { translate } from 'react-i18next';
import { IManualBookingFormData } from 'models/shop/manualbooking';
import { RightSideModalFooter } from 'components/RightSideModal';
import {
	deleteURLSearchParams,
	formatDuration,
	readMultipleURLSearchParams,
	thumbUrl,
	timePickerProps,
} from 'helpers/common';
import { PortalPageSection } from 'components/PortalStructure';
import renderIcon from 'helpers/renderIcon';
import { Field } from 'redux-form';
import moment = require('moment');
import { BookingDatePicker, BookingInputField, BookingSelectMenu } from 'components/FormControls';
import { IAddBookingItemRequest, IBookingUnavailableReason, IFormAddBookingItemRequest } from 'models/bookappointment';
import { IOfficeMember } from 'models/shop/members';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { isNumber, priceFieldNormalization, scrollErrorIntoView } from 'helpers/validationattributes';
import { getPriceFromBookingItem } from '../helpers';
import { OrderPaymentStatus } from 'models';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
require('./style.scss');

interface IOwnProps {
	appointment: IAppointmentWithCustomer;
	officeId?: string;
}

interface IState {
	originalServicePrice: string; // original booking template price without discounts
	servicePrice: string; // booking template price with discounts
	openedChildServiceIds: string[];
	selectedServiceBookingConflicts: IBookingUnavailableReason[];
	conflictsOpened: boolean;
}

export interface IEditServiceScreenQueryParams extends InjectedQueryParams {
	bookingItemIndex?: string;
}

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ITranslateProps &
	InjectedFormProps &
	InjectedManualBookingModalProps;

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class EditServiceScreenComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			originalServicePrice: null,
			servicePrice: null,
			openedChildServiceIds: [],
			selectedServiceBookingConflicts: [],
			conflictsOpened: false,
		};

		this.onResourceChangeService = this.onResourceChangeService.bind(this);
		this.calculateBundleDuration = this.calculateBundleDuration.bind(this);
		this.saveChangesFunction = this.saveChangesFunction.bind(this);
		this.onDiscountStrategyChange = this.onDiscountStrategyChange.bind(this);
		this.priceFieldValidation = this.priceFieldValidation.bind(this);

		this.formRef = React.createRef();
	}

	formRef: React.Ref<HTMLFormElement>;

	componentDidMount(): void {
		if (this.props.params.bookingItemIndex) {
			const currentBookingItem: IAddBookingItemRequest =
				this.props.currentFormValues?.bookingItems?.[this.props.params.bookingItemIndex];

			if (!currentBookingItem) {
				this.props.setScreen(ManualBookingScreen.Main);
			} else {
				// set entire selected bookingItem to be currentService (take original price instead price, the price value can already include discount)
				const cbi: IFormAddBookingItemRequest = JSON.parse(JSON.stringify(currentBookingItem));
				const currentBookingTemplate = this.props.shopBookingTemplates.services.find(
					(s) => s.id === cbi.bookingTemplateId,
				);
				cbi.price = String(getPriceFromBookingItem(cbi, currentBookingTemplate));
				this.props.change('currentService', cbi);
				this.setState({
					originalServicePrice: currentBookingTemplate.originalPrice.toString(),
					servicePrice: currentBookingTemplate.price.toString(),
				});

				if (this.props.currentFormValues?.bookingItemConflicts?.length) {
					const bookingItemConflicts = this.props.currentFormValues.bookingItemConflicts.filter(
						(bic) => bic.bookingTemplateId === cbi.bookingTemplateId,
					);

					this.setState({ selectedServiceBookingConflicts: bookingItemConflicts });
				}
			}
		} else {
			this.props.setScreen(ManualBookingScreen.Main);
		}
	}

	componentWillUnmount(): void {
		this.props.change('currentService', '');
		deleteURLSearchParams(['bookingItemIndex']);
	}

	private onResourceChangeService = (event: React.ChangeEvent<HTMLInputElement>, variation: IServicesList) => {
		const newResourceId = event.target.value;
		const newCurrentService: IFormAddBookingItemRequest = { ...this.props.currentFormValues.currentService };

		// If same variaton update resourceId, if not update bookingTemplateId, price and duration aswell
		if (variation.id === this.props.currentFormValues.currentService.bookingTemplateId) {
			newCurrentService.resourceId = newResourceId;
			this.props.change('currentService', newCurrentService);
		} else {
			newCurrentService.resourceId = newResourceId;
			newCurrentService.bookingTemplateId = variation.id;
			newCurrentService.originalPrice = variation.originalPrice;

			this.setState({
				originalServicePrice: variation.originalPrice.toString(),
				servicePrice: variation.price.toString(),
			});
			this.props.change('currentService', newCurrentService);

			// Timeout because validation function gets triggered before change and it compares the old price giving a validation error if new variation price is higher than previous variation
			setTimeout(() => {
				this.props.change('currentService.price', variation.price.toString());
				// Change duration with price so it is synced
				this.props.change('currentService.duration', variation.duration);
			}, 100);
		}
	};

	private onResourceChangeBundle = (newResourceId: string, index: number) => {
		const newAddBookingItemChild: IAddBookingItemRequest = {
			...this.props.currentFormValues.currentService.childrenAddBookingItemRequest[index],
			resourceId: newResourceId,
		};
		this.props.change(`currentService.childrenAddBookingItemRequest.[${index}]`, newAddBookingItemChild);
	};

	private calculateBundleDuration = (index: number, type: 'add' | 'subtract') => {
		const currentBundle = { ...this.props.currentFormValues.currentService };
		const childToChange = currentBundle.childrenAddBookingItemRequest[index];
		if (type === 'add') {
			childToChange.duration = childToChange.duration + 300;
		} else if (type === 'subtract' && childToChange.duration > 300) {
			childToChange.duration = childToChange.duration - 300;
		}

		let bundleDuration = 0;

		this.props.currentFormValues.currentService.childrenAddBookingItemRequest.map((cbir) => {
			bundleDuration = bundleDuration + cbir.duration;
		});

		currentBundle.duration = bundleDuration;
		this.props.change('currentService', currentBundle);
	};

	private saveChangesFunction = () => {
		const currentService: IFormAddBookingItemRequest = JSON.parse(
			JSON.stringify(this.props.currentFormValues.currentService),
		);
		const isBundle: boolean = currentService?.childrenAddBookingItemRequest?.length ? true : false;

		if (isBundle) {
			const bundleSchedule = moment(currentService.schedule).clone();
			let prevDuration = null;
			currentService.childrenAddBookingItemRequest.forEach((cbir, idx) => {
				if (idx === 0) {
					cbir.schedule = bundleSchedule.toISOString();
				} else {
					cbir.schedule = bundleSchedule.add(prevDuration, 'seconds').toISOString();
				}

				prevDuration = cbir.duration;
			});
		}

		// first condition: check if field price is not the same as originalPrice (only on edit manual booking)
		// second condition: check if field price is not the same as original service price
		// third condition: check if the service has a booking template discount on it and then
		// check if the field price is the same as the original service price (lets user input the original price without discount and apply it to the booking item)
		if (
			(currentService?.originalPrice && Number(currentService.price) !== currentService.originalPrice) ||
			Number(currentService.price) !== Number(this.state.originalServicePrice) ||
			(this.state.originalServicePrice !== this.state.servicePrice &&
				Number(currentService.price) == Number(this.state.originalServicePrice))
		) {
			currentService.discountPrice = parseFloat(Number(currentService.price).toFixed(2));
		} else {
			currentService.discountPrice = undefined;
		}

		currentService.price = undefined;
		currentService.discountStrategyId = undefined;

		this.props.change(`bookingItems[${this.props.params.bookingItemIndex}]`, currentService);
		this.props.setScreen(ManualBookingScreen.Main);
	};

	private onDiscountStrategyChange = (discountStrategyId: string) => {
		// take first item as they all have the same discount rate / percentage
		const discountStrategy = this.props.discountStrategies?.[discountStrategyId]?.[0];

		if (discountStrategy && this.state?.originalServicePrice) {
			let itemPrice = Number(this.state.originalServicePrice);
			if (discountStrategy?.isDiscountPercentage) {
				itemPrice = itemPrice - itemPrice * (discountStrategy.discountValue / 100);
				itemPrice = parseFloat(itemPrice.toFixed(2));
			} else {
				itemPrice = parseFloat((itemPrice - discountStrategy.discountValue).toFixed(2));
			}

			if (itemPrice > 0) {
				this.props.change('currentService.price', itemPrice.toString());
			} else {
				this.props.change('currentService.price', this.state.originalServicePrice);
			}
		} else {
			this.props.change('currentService.price', this.state.originalServicePrice);
		}
	};

	private priceFieldValidation = (value) => {
		const { t } = this.props;

		// Price cannot be negative
		if (value < 0) {
			return t('BookingTemplateBusinessRules_PriceNegative');
		} else {
			return undefined;
		}
	};

	public render() {
		const { t, params, timezone, shopOrder, handleSubmit } = this.props;
		const currentBookingItem: IAddBookingItemRequest =
			this.props.currentFormValues?.bookingItems?.[params?.bookingItemIndex] || null;
		const isBundle: boolean = currentBookingItem?.childrenAddBookingItemRequest?.length ? true : false;
		const bt = this.props.shopBookingTemplates?.servicesById?.[currentBookingItem?.bookingTemplateId] || null;
		const sg = this.props.serviceGroups?.serviceGroupsById?.[bt?.serviceGroupId] || null;
		const parentSg = this.props.serviceGroups?.serviceGroupsById?.[sg?.parentId] || null;
		const variations =
			bt &&
			(shopOrder?.order?.paymentStatus !== OrderPaymentStatus.Verified
				? this.props.shopBookingTemplates.services.filter((s) => s.variantParentId === bt.variantParentId)
				: [bt]);
		const resource = this.props?.currentOffice.staff.find((res) => res.id === currentBookingItem?.resourceId) || null;
		let resourceCounter = 0;

		return (
			<RightSideModal
				size="small"
				header={
					<>
						<RightSideModalTitle
							title={
								isBundle
									? t('ManualBookingModal_EditService_Main_BundleTitle')
									: t('ManualBookingModal_EditService_Main_ServiceTitle')
							}
							closeModalClick={
								(this.props.mode === 'add' && !this.props.dirty) ||
								(this.props?.mode === 'edit' && !this.props?.currentFormValues?.isEditModeEnabled)
									? this.props.closeModal
									: this.props.startCloseModal
							}
							backButtonClick={() => {
								// this.props.change('currentService', undefined);
								this.props.setScreen(ManualBookingScreen.Main);
							}}
						/>
					</>
				}
				footer={
					<>
						<RightSideModalFooter
							leftButtonClick={() => {
								if (this.props.mode === 'add') {
									this.props.array.remove('bookingItems', Number(params.bookingItemIndex));
								} else {
									const bookingItems: IFormAddBookingItemRequest[] = JSON.parse(
										JSON.stringify(this.props.currentFormValues.bookingItems),
									);
									// shop order must have atleast 1 booking item after update
									// TODO: Handle the UI better if only 1 item
									if (bookingItems?.filter((bi) => !bi.deleted)?.length > 1) {
										bookingItems[Number(params.bookingItemIndex)].deleted = true;
										this.props.change('bookingItems', bookingItems);
									}
								}

								this.props.setScreen(ManualBookingScreen.Main);
							}}
							leftButtonText={
								((this.props.mode === 'edit' && this.props.currentFormValues?.bookingItems?.length > 1) ||
									this.props.mode === 'add') &&
								(isBundle
									? t('ManualBookingModal_EditService_RemoveBundle')
									: t('ManualBookingModal_EditService_RemoveService'))
							}
							leftButtonMobileText={t('BSPortal_Common_Remove')}
							leftButtonCustomClass={'red'}
							rightButtonClick={() => {
								(this.formRef as any)?.current?.requestSubmit();
							}}
							rightButtonText={t('ManualBookingModal_EditService_SaveChanges')}
							rightButtonMobileText={t('BSPortal_Common_Save')}
						/>
					</>
				}
			>
				<form ref={this.formRef} onSubmit={handleSubmit(this.saveChangesFunction)}>
					<PortalPageSection
						className={`edit-service-screen__serviceWrapper ${
							this.props?.currentFormValues?.isPaidOnline ? 'uneditable' : ''
						}`}
						onClick={() =>
							!this.props?.currentFormValues?.isPaidOnline
								? this.props.setScreen(ManualBookingScreen.SelectService, {
										serviceSelectMode: 'swap',
										prevBookingItemIndex: this.props.params.bookingItemIndex,
								  })
								: null
						}
					>
						<p className="edit-service-screen__serviceName">
							{parentSg?.value} <span className="edit-service-screen__serviceNameSeparator">/</span> {bt?.name}
						</p>
						{!this.props?.currentFormValues?.isPaidOnline ? renderIcon('arrow_right_round', 24, 24) : null}
					</PortalPageSection>

					{this.state?.selectedServiceBookingConflicts?.length ? (
						<div className="edit-service-screen__bookingConflictsWrapper">
							<div
								className="edit-service-screen__bookingConflictInfoWrapper"
								onClick={() => {
									this.setState((state) => ({ conflictsOpened: !state.conflictsOpened }));
								}}
							>
								<div className="edit-service-screen__bookingConflictInfo">
									{renderIcon('warning', undefined, undefined, 'edit-service-screen__bookingConflict__icon')}
									<span className="edit-service-screen__bookingConflict__text">
										{t(`ManualBookingModal_EditService_BookingConflict_Text`, { resourceName: resource?.name })}
									</span>
								</div>
								{renderIcon(
									'dropdown',
									16,
									16,
									`edit-service-screen__bookingConflictDropdownIcon ${this.state?.conflictsOpened ? 'opened' : ''}`,
								)}
							</div>
							{this.state?.conflictsOpened ? (
								<div className="edit-service-screen__bookingConflictsContainer">
									{this.state?.selectedServiceBookingConflicts.map((scbs) => {
										return (
											<div className="edit-service-screen__bookingConflictTime" key={scbs?.schedule?.toString()}>
												{momentWithTimezone(timezone, scbs?.schedule)
													.format('LLL')
													.toString()}
											</div>
										);
									})}
								</div>
							) : null}
						</div>
					) : null}

					<div className="edit-service-screen__fieldsWrapper">
						<div className="row">
							<div className="col-12">
								<h3 className="edit-service-screen__sectionTitle">
									{t('ManualBookingModal_EditService_TimeAndPrice')}
								</h3>
							</div>
							<div className="col-md-6 col-12">
								<div className="edit-service-screen__timeFieldWrapper">
									<Field
										name={'currentService.schedule'}
										type="text"
										component={BookingDatePicker}
										information={t('Shop_Appointments_TimeOfTheAppointment')}
										label={t('Shop_Appointments_TimeOfTheAppointment')}
										{...timePickerProps(moment().startOf('day').toDate(), moment().endOf('day').toDate())} // TODO: fix as appointment can possibly go into the next day
										timezone={timezone}
									/>
								</div>
							</div>
							<div className="col-md-6 col-12">
								<div className="edit-service-screen__durationFieldWrapper">
									<Field
										name={'currentService.duration'}
										type="text"
										component={BookingInputField}
										information={t('User_ShopCard_Services_Duration')}
										label={t('User_ShopCard_Services_Duration')}
										isDisabled={true}
										className={'edit-service-screen__durationField'}
									/>
								</div>
								<div className={`edit-service-screen__durationFieldElements ${isBundle ? 'disabled' : ''}`}>
									<div
										className={`edit-service-screen__durationMinusButton ${
											this.props?.currentFormValues?.currentService?.duration <= 300 ? 'disabled' : ''
										}`}
										onClick={() => {
											!isBundle &&
												this.props?.currentFormValues?.currentService?.duration > 300 &&
												this.props.change(
													'currentService.duration',
													this.props.currentFormValues.currentService.duration - 300,
												);
										}}
									>
										{renderIcon('nikaMinus', 24, 24, undefined)}
									</div>
									<span className="edit-service-screen__durationText">
										{formatDuration(this.props?.currentFormValues?.currentService?.duration)}
									</span>
									<div
										className="edit-service-screen__durationPlusButton"
										onClick={() => {
											!isBundle &&
												this.props.change(
													'currentService.duration',
													this.props.currentFormValues.currentService.duration + 300,
												);
										}}
									>
										{renderIcon('nikaPlus', 24, 24, undefined)}
									</div>
								</div>
							</div>
							<div className="col-md-6 col-12">
								<Field
									name={'currentService.price'}
									type="text"
									component={BookingInputField}
									information={t('Shop_ManageShop_Products_Label_Price')}
									label={t('Shop_ManageShop_Products_Label_Price')}
									isDisabled={
										this.props?.currentFormValues?.currentService?.discountStrategyId ||
										this.props?.currentFormValues?.isPaidOnline
											? true
											: false
									}
									className={'edit-service-screen__fieldMargin'}
									validate={[isNumber, this.priceFieldValidation]}
									min={0}
									step={0.01}
									inputMode={'decimal'}
									normalize={priceFieldNormalization}
								/>
							</div>
							<div className="col-md-6 col-12">
								<div className={'edit-service-screen__fieldMargin edit-service-screen__discountField'}>
									<Field
										name={'currentService.discountStrategyId'}
										type="text"
										component={BookingSelectMenu}
										items={this.props.discountStrategyDropdownValues}
										information={t('Shop_Promotions_New_DiscountValue_Discount')}
										label={t('Shop_Promotions_New_DiscountValue_Discount')}
										onChange={(_event, value) => this.onDiscountStrategyChange(value)}
										isDisabled={
											(!this.props?.currentFormValues?.currentService?.discountStrategyId &&
												this.props?.currentFormValues?.currentService &&
												this.state.originalServicePrice !== this.props.currentFormValues.currentService.price) ||
											this.props?.currentFormValues?.isPaidOnline
												? true
												: false
										}
										emptyValueLabel={t('ManualBookingModal_EditService_NoDiscount')}
									/>
								</div>
							</div>
						</div>

						<div className="row">
							<div className="col-12">
								<h3 className="edit-service-screen__staffServicesTitle">
									{isBundle ? t('ManualBookingModal_EditService_IncludedServices') : t('User_ShopCard_Services_Staff')}
								</h3>
							</div>
							{isBundle
								? currentBookingItem.childrenAddBookingItemRequest.map((cbir, cIdx) => {
										const childBt = this.props.shopBookingTemplates?.servicesById?.[cbir?.bookingTemplateId] || null;
										const childSg = this.props.serviceGroups?.serviceGroupsById?.[childBt?.serviceGroupId] || null;
										const childParentSg = this.props.serviceGroups?.serviceGroupsById?.[childSg?.parentId] || null;

										return (
											<div className="col-12" key={currentBookingItem.bookingTemplateId + cbir.bookingTemplateId}>
												<PortalPageSection className="edit-service-screen__childBookingItem">
													<div className="row">
														<div className="col-12">
															<div
																className="edit-service-screen__childNameContainer"
																onClick={() => {
																	const currentOpenedItems = [...this.state.openedChildServiceIds];
																	const clickedItem = currentOpenedItems.find(
																		(ocsi) => ocsi === cbir?.bookingTemplateId,
																	);
																	if (clickedItem) {
																		const newChildServiceIds = currentOpenedItems.filter(
																			(ocsi) => ocsi !== clickedItem,
																		);
																		this.setState({ openedChildServiceIds: newChildServiceIds });
																	} else {
																		currentOpenedItems.push(cbir.bookingTemplateId);
																		this.setState({ openedChildServiceIds: currentOpenedItems });
																	}
																}}
															>
																<h3 className="edit-service-screen__childName">
																	{childParentSg?.value}{' '}
																	<span className="edit-service-screen__childNameSeparator">/</span> {childBt?.name}
																</h3>
																{renderIcon(
																	'dropdown',
																	16,
																	16,
																	`edit-service-screen__childNameDropdownIcon ${
																		this.state?.openedChildServiceIds?.includes(cbir?.bookingTemplateId) ? 'opened' : ''
																	}`,
																)}
															</div>
														</div>
														{this.state?.openedChildServiceIds?.includes(cbir?.bookingTemplateId) && (
															<>
																<div className="col-12">
																	<h4 className="edit-service-screen__childDurationTitle">
																		{t('ManualBookingModal_EditService_DurationOfService')}
																	</h4>
																</div>
																<div className="col-12">
																	<div className="row">
																		<div className="col-md-6 col-12">
																			<Field
																				name={`currentService.childrenAddBookingItemRequest.[${cIdx}].duration`}
																				type="text"
																				component={BookingInputField}
																				information={t('User_ShopCard_Services_Duration')}
																				label={t('User_ShopCard_Services_Duration')}
																				isDisabled={true}
																				className={'edit-service-screen__durationField'}
																			/>
																			<div className="edit-service-screen__durationFieldElements">
																				<div
																					className={`edit-service-screen__durationMinusButton ${
																						this.props?.currentFormValues?.currentService
																							?.childrenAddBookingItemRequest?.[cIdx]?.duration <= 300
																							? 'disabled'
																							: ''
																					}`}
																					onClick={() => {
																						this.calculateBundleDuration(cIdx, 'subtract');
																					}}
																				>
																					{renderIcon('nikaMinus', 24, 24, undefined)}
																				</div>
																				<span className="edit-service-screen__durationText">
																					{formatDuration(
																						this.props?.currentFormValues?.currentService
																							?.childrenAddBookingItemRequest?.[cIdx]?.duration,
																					)}
																				</span>
																				<div
																					className="edit-service-screen__durationPlusButton"
																					onClick={() => {
																						this.calculateBundleDuration(cIdx, 'add');
																					}}
																				>
																					{renderIcon('nikaPlus', 24, 24, undefined)}
																				</div>
																			</div>
																		</div>
																		<div className="col-12">
																			<h4 className="edit-service-screen__childResourcesTitle">
																				{t('User_ShopCard_Services_Staff')}
																			</h4>
																		</div>
																	</div>
																</div>
																{childBt.resources.map((cRes: IOfficeMember, crIdx: number) => {
																	const childResFullName = cRes ? cRes.name.split(' ') : null;

																	return (
																		<div className="col-md-6 col-12" key={cbir.bookingTemplateId + cRes.id}>
																			<div
																				className={`edit-service-screen__resourceContainer ${
																					crIdx > 1 ? 'resourceMargin' : ''
																				}`}
																			>
																				<label className="edit-service-screen__resourceButton">
																					<input
																						key={cbir.bookingTemplateId + cRes.id}
																						name={cRes.name + cbir.bookingTemplateId}
																						value={cbir.bookingTemplateId + cRes.id}
																						type="radio"
																						checked={
																							cRes.id ===
																							this.props?.currentFormValues?.currentService
																								?.childrenAddBookingItemRequest?.[cIdx]?.resourceId
																						}
																						onChange={() => this.onResourceChangeBundle(cRes.id, cIdx)} // Bundles do not have variations (always a defined booking template)
																					/>
																					<div className="edit-service-screen__resourceWrapper">
																						<CoverPicture
																							imageUrl={thumbUrl(cRes?.picture, 'medium') || ''}
																							hasPicture={Boolean(cRes?.picture)}
																						/>
																						<div className="edit-service-screen__resourceInfoWrapper">
																							<p className="edit-service-screen__resourceName">
																								{childResFullName?.[0]}{' '}
																								{childResFullName?.[childResFullName?.length - 1].charAt(0)}.
																							</p>
																							<p className="edit-service-screen__resourceTitle">
																								{(cRes as IOfficeMember)?.jobTitle?.substring(0, 14)}
																							</p>
																						</div>
																					</div>
																				</label>
																			</div>
																		</div>
																	);
																})}
															</>
														)}
													</div>
												</PortalPageSection>
											</div>
										);
								  })
								: variations &&
								  variations.map((ser) => {
										return ser.resources.map((res: IOfficeMember) => {
											const resFullName = res ? res.name.split(' ') : null;
											resourceCounter = resourceCounter + 1;
											return (
												<div className="col-md-6 col-12" key={currentBookingItem.bookingTemplateId + res.id}>
													<div
														className={`edit-service-screen__resourceContainer ${
															resourceCounter > 2 ? 'resourceMargin' : ''
														}`}
													>
														<label className="edit-service-screen__resourceButton">
															<input
																key={res.id}
																name={res.name}
																value={res.id}
																type="radio"
																checked={res.id === this.props?.currentFormValues?.currentService?.resourceId}
																onChange={(e) => this.onResourceChangeService(e, ser)}
															/>
															<div className="edit-service-screen__resourceWrapper">
																<CoverPicture
																	imageUrl={thumbUrl(res?.picture, 'medium') || ''}
																	hasPicture={Boolean(res?.picture)}
																/>
																<div className="edit-service-screen__resourceInfoWrapper">
																	<p className="edit-service-screen__resourceName">
																		{resFullName?.[0]} {resFullName?.[resFullName?.length - 1].charAt(0)}.
																	</p>
																	<p className="edit-service-screen__resourceTitle">
																		{(res as IOfficeMember)?.jobTitle?.substring(0, 14)}
																	</p>
																</div>
															</div>
														</label>
													</div>
												</div>
											);
										});
								  })}
						</div>
					</div>
				</form>
			</RightSideModal>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: IManualBookingFormData = null;
	const currentOffice = state?.manageShop?.shop?.offices.find((off) => off.id === ownProps.officeId) || null;
	const params: IEditServiceScreenQueryParams = readMultipleURLSearchParams(['bookingItemIndex']);

	if (state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN] && state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values) {
		currentFormValues = state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values;
	}

	const groupedDiscountStrategies = state?.discountStrategyReducer?.groupedDiscountStrategies;
	const discountStrategyDropdownValues: { key: string; value: string }[] = groupedDiscountStrategies
		? Object.keys(groupedDiscountStrategies).map((key) => {
				return {
					key,
					value: groupedDiscountStrategies?.[key]?.[0].title || null,
				};
		  })
		: [];

	return {
		currentFormValues,
		shopcard: state.shopcard,
		manageShop: state.manageShop,
		serviceGroups: state.serviceGroups,
		shopBookingTemplates: state.shopBookingTemplates,
		currentOffice,
		params,
		discountStrategyDropdownValues,
		discountStrategies: groupedDiscountStrategies,
		shopOrder: state.shopOrderCard.shopOrder,
	};
};

export const EditServiceScreen = connect(stateToProps)(
	reduxForm({
		form: c.FORM_NAME_MANUAL_BOOKING_PLAN,
		enableReinitialize: false,
		destroyOnUnmount: false,
		onSubmitFail: () => scrollErrorIntoView(),
	})(EditServiceScreenComponent),
);
